import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Organisation } from '../../core/models/organisation.model';
import { OrganisationService } from '../../core/services/organisation.service';
import { LookupService } from '../../core/services/lookup.service';
import { StatusLookup } from '../../core/models/lookup.model';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-client-onboarding',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, ConfirmDialogComponent],
  templateUrl: './client-onboarding.component.html',
  styleUrls: ['./client-onboarding.component.css']
})
export class ClientOnboardingComponent implements OnInit {
  private fb = inject(FormBuilder);
  private organisationService = inject(OrganisationService);
  private lookupService = inject(LookupService);

  organisations: Organisation[] = [];
  filteredOrganisations: Organisation[] = [];
  statuses: StatusLookup[] = [];
  selectedOrganisation: Organisation | null = null;
  searchTerm = '';
  showForm = false;
  isLoading = false;
  isSaving = false;
  errorMessage = '';
  successMessage = '';
  organisationPendingDelete: Organisation | null = null;
  readonly knownOperatorTypes = ['bus', 'truck', 'taxi'];
  operatorTypeChanged = false;

  organisationForm = this.fb.group({
    orgName: ['', Validators.required],
    orgRegistrationNumber: [''],
    powerlynxLocationID: [null as number | null, [Validators.min(1), Validators.pattern(/^[1-9]\d*$/)]],
    operatorCode: [''],
    operatorType: [''],
    orgLogo: [''],
    statusID: [1]
  });

  checklist = [
    { icon: 'business', label: 'Operator profile', detail: 'Organisation, registration, logo, Powerlynx location, and activation status' },
    { icon: 'people', label: 'Operator users', detail: 'Create users and assign operator permissions' },
    { icon: 'devices', label: 'Device kit', detail: 'Prepare trackers, SIMs, and installation batches' },
    { icon: 'route', label: 'Routes and trips', detail: 'Create first routes, stops, vehicles, and schedules' },
    { icon: 'monitor_heart', label: 'Go-live monitoring', detail: 'Watch heartbeat, GPS freshness, and alerts' }
  ];

  ngOnInit(): void {
    this.loadStatuses();
    this.loadOrganisations();
  }

  loadStatuses(): void {
    this.lookupService.getStatuses().subscribe({
      next: statuses => {
        this.statuses = statuses;
      },
      error: error => {
        console.error('Unable to load statuses:', error);
        this.statuses = [];
      }
    });
  }

  loadOrganisations(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.organisationService.getOrganisations().subscribe({
      next: organisations => {
        this.organisations = organisations;
        this.applyFilters();
        this.isLoading = false;
      },
      error: error => {
        console.error('Unable to load organisations:', error);
        this.errorMessage = 'Unable to load operator organisations from the API.';
        this.isLoading = false;
      }
    });
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    this.filteredOrganisations = !term
      ? [...this.organisations]
      : this.organisations.filter(organisation => [
          organisation.orgName,
          organisation.organisationName,
          this.getPowerlynxLocationID(organisation),
          organisation.operatorCode,
          organisation.operatorType,
          organisation.orgRegistrationNumber,
          organisation.organisationRegistrationNumber,
          organisation.statusName,
          this.getStatusName(organisation)
        ].some(value => String(value || '').toLowerCase().includes(term)));
  }

  getStatusName(organisation: Organisation): string {
    return organisation.statusName
      || this.statuses.find(status => status.statusID === Number(organisation.statusID))?.statusName
      || (organisation.statusID ? `Status ${organisation.statusID}` : '-');
  }

  getOperatorCode(organisation: Organisation): string {
    return organisation.operatorCode?.trim() || '-';
  }

  getOperatorTypeLabel(organisation: Organisation): string {
    return organisation.operatorType
      ? organisation.operatorType.replace(/_/g, ' ')
      : 'Not set';
  }

  getOperatorTypeOptions(): string[] {
    const current = this.organisationForm.value.operatorType || this.selectedOrganisation?.operatorType || '';
    return [...new Set([...this.knownOperatorTypes, current].filter(Boolean))];
  }

  getPowerlynxLocationID(organisation: Organisation): string {
    const value = organisation.powerlynxLocationID ?? organisation.powerlynxLocationId;
    return value ? String(value) : '-';
  }

  startCreate(): void {
    this.selectedOrganisation = null;
    this.organisationForm.reset({ orgName: '', orgRegistrationNumber: '', powerlynxLocationID: null, operatorCode: '', operatorType: '', orgLogo: '', statusID: 1 });
    this.operatorTypeChanged = false;
    this.showForm = true;
    this.successMessage = '';
    this.errorMessage = '';
  }

  editOrganisation(organisation: Organisation): void {
    this.selectedOrganisation = organisation;
    this.organisationForm.patchValue({
      orgName: organisation.orgName || organisation.organisationName || '',
      orgRegistrationNumber: organisation.orgRegistrationNumber || organisation.organisationRegistrationNumber || '',
      powerlynxLocationID: organisation.powerlynxLocationID ?? organisation.powerlynxLocationId ?? null,
      operatorCode: organisation.operatorCode || '',
      operatorType: organisation.operatorType || '',
      orgLogo: organisation.orgLogo || organisation.organisationLogo || '',
      statusID: organisation.statusID || 1
    });
    this.showForm = true;
    this.successMessage = '';
    this.errorMessage = '';
    this.operatorTypeChanged = false;
  }

  saveOrganisation(): void {
    if (this.organisationForm.invalid) {
      this.organisationForm.markAllAsTouched();
      return;
    }

    const id = this.selectedOrganisation?.organisationID;
    const payload = this.buildOrganisationPayload();
    this.isSaving = true;
    this.errorMessage = '';

    const request$ = id
      ? this.organisationService.updateOrganisation(id, payload)
      : this.organisationService.createOrganisation(payload);

    request$.subscribe({
      next: () => {
        const typeNotice = id && payload.operatorType !== (this.selectedOrganisation?.operatorType || null)
          ? ' Operator users may need to sign in again before type-specific feature access is refreshed.'
          : '';
        this.successMessage = (id ? 'Operator updated.' : 'Operator created.') + typeNotice;
        this.isSaving = false;
        this.showForm = false;
        this.selectedOrganisation = null;
        this.loadOrganisations();
      },
      error: error => {
        console.error('Unable to save organisation:', error);
        this.errorMessage = this.getSaveErrorMessage(error);
        this.isSaving = false;
      }
    });
  }

  deleteOrganisation(organisation: Organisation): void {
    this.organisationPendingDelete = organisation;
  }

  cancelDeleteOrganisation(): void {
    this.organisationPendingDelete = null;
  }

  confirmDeleteOrganisation(): void {
    const organisation = this.organisationPendingDelete;
    if (!organisation) {
      return;
    }

    this.organisationService.deleteOrganisation(organisation.organisationID).subscribe({
      next: () => {
        this.successMessage = 'Operator deleted.';
        this.organisationPendingDelete = null;
        this.loadOrganisations();
      },
      error: error => {
        console.error('Unable to delete organisation:', error);
        this.errorMessage = 'Unable to delete the operator organisation.';
        this.organisationPendingDelete = null;
      }
    });
  }

  cancelForm(): void {
    this.showForm = false;
    this.selectedOrganisation = null;
  }

  useCompanyLogo(event: Event): void {
    const image = event.target as HTMLImageElement;
    if (image.src.endsWith('assets/branding/logo.png')) {
      return;
    }

    image.src = 'assets/branding/logo.png';
  }

  private buildOrganisationPayload(): Partial<Organisation> {
    const value = this.organisationForm.value;
    const now = new Date().toISOString();

    return {
      organisationID: this.selectedOrganisation?.organisationID || 0,
      orgName: value.orgName || '',
      orgRegistrationNumber: value.orgRegistrationNumber || null,
      powerlynxLocationID: value.powerlynxLocationID ? Number(value.powerlynxLocationID) : null,
      operatorCode: value.operatorCode?.trim() || null,
      operatorType: this.normalizeOperatorType(value.operatorType),
      orgLogo: value.orgLogo || null,
      orgCreateDate: this.selectedOrganisation?.orgCreateDate || now,
      orgModifyDate: now,
      statusID: Number(value.statusID || this.selectedOrganisation?.statusID || 1)
    };
  }

  private normalizeOperatorType(value: string | null | undefined): string | null {
    const normalized = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
    return normalized || null;
  }

  private getSaveErrorMessage(error: any): string {
    const message = String(
      (typeof error?.error === 'string' ? error.error : '')
      || error?.error?.message
      || error?.error?.title
      || error?.message
      || ''
    ).trim();
    if (/powerlynx.*location|location.*powerlynx|duplicate|already/i.test(message)) {
      return message;
    }

    if (error?.status === 409) {
      return 'Another organisation already uses this Powerlynx Location ID.';
    }

    return 'Unable to save the operator organisation.';
  }
}
